import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, 'A senha deve ter pelo menos 8 caracteres')
    .regex(/(?=.*[a-z])/, 'A senha deve conter pelo menos uma letra minúscula')
    .regex(/(?=.*[A-Z])/, 'A senha deve conter pelo menos uma letra maiúscula')
    .regex(/(?=.*\d)/, 'A senha deve conter pelo menos um número')
    .regex(/(?=.*[@$!%*?&])/, 'A senha deve conter pelo menos um símbolo (@$!%*?&)'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [showWarning, setShowWarning] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  // Prevenir que o usuário saia sem definir a senha
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (sessionReady && showWarning) {
        e.preventDefault();
        e.returnValue = 'Você precisa definir uma nova senha antes de sair. Se sair agora, sua conta ficará sem senha válida.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionReady, showWarning]);

  useEffect(() => {
    console.log('🔍 Reset Password - Análise completa da URL');
    console.log('🔍 URL completa:', window.location.href);
    console.log('🔍 Hash:', window.location.hash);
    console.log('🔍 Search:', window.location.search);
    console.log('🔍 Pathname:', window.location.pathname);
    
    const processPasswordReset = async () => {
      let hasValidSession = false;
      
      // 1. Primeiro, verificar se há sessão ativa
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (session?.user && !error) {
          console.log('✅ Sessão ativa encontrada - permitindo reset direto');
          hasValidSession = true;
          
          toast({
            title: "Reset autorizado",
            description: "Você pode alterar sua senha usando a sessão atual.",
            variant: "default",
          });
          return;
        }
      } catch (error) {
        console.error('❌ Erro ao verificar sessão:', error);
      }
      
      // 2. Se não há sessão, tentar extrair tokens da URL  
      const extractTokens = () => {
        console.log('🔍 Iniciando extração de tokens...');
        console.log('🔍 URL completa:', window.location.href);
        console.log('🔍 Search params:', window.location.search);
        
        // Método 1: Query parameters (formato atual do Supabase)
        if (window.location.search) {
          const queryParams = new URLSearchParams(window.location.search);
          const tokenHash = queryParams.get('token_hash');
          const type = queryParams.get('type');
          
          console.log('🔍 token_hash encontrado:', tokenHash);
          console.log('🔍 type encontrado:', type);
          
          if (tokenHash && type === 'recovery') {
            console.log('✅ Token hash encontrado nos query params (formato atual)');
            return { tokenHash, type, format: 'new' };
          }
          
          // Fallback para formato antigo
          const accessToken = queryParams.get('access_token');
          const refreshToken = queryParams.get('refresh_token');
          
          if (accessToken && refreshToken && type === 'recovery') {
            console.log('✅ Tokens encontrados nos query params (formato antigo)');
            return { accessToken, refreshToken, type, format: 'old' };
          }
        }
        
        // Método 2: Hash parameters (formato antigo)
        if (window.location.hash) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          const type = hashParams.get('type');
          
          if (accessToken && refreshToken && type === 'recovery') {
            console.log('✅ Tokens encontrados no hash (formato antigo)');
            return { accessToken, refreshToken, type, format: 'old' };
          }
        }
        
        // Método 3: Regex na URL completa (fallback)
        const fullUrl = window.location.href;
        
        // Novo formato com token_hash
        const tokenHashMatch = fullUrl.match(/[#&?]token_hash=([^&#]+)/);
        const typeMatch = fullUrl.match(/[#&?]type=([^&#]+)/);
        
        if (tokenHashMatch && typeMatch) {
          const tokenHash = decodeURIComponent(tokenHashMatch[1]);
          const type = decodeURIComponent(typeMatch[1]);
          
          if (type === 'recovery') {
            console.log('✅ Token hash encontrado via regex (formato atual)');
            return { tokenHash, type, format: 'new' };
          }
        }
        
        // Formato antigo via regex
        const accessMatch = fullUrl.match(/[#&?]access_token=([^&#]+)/);
        const refreshMatch = fullUrl.match(/[#&?]refresh_token=([^&#]+)/);
        
        if (accessMatch && refreshMatch && typeMatch) {
          const accessToken = decodeURIComponent(accessMatch[1]);
          const refreshToken = decodeURIComponent(refreshMatch[1]);
          const type = decodeURIComponent(typeMatch[1]);
          
          if (type === 'recovery') {
            console.log('✅ Tokens encontrados via regex (formato antigo)');
            return { accessToken, refreshToken, type, format: 'old' };
          }
        }
        
        console.log('❌ Nenhum token encontrado');
        return null;
      };
      
      const tokens = extractTokens();
      
      // 3. Se encontrou tokens, configurar sessão
      if (tokens && !hasValidSession) {
        console.log('🔄 Configurando sessão com tokens de recuperação');
        console.log('🔄 Tokens objeto:', JSON.stringify(tokens, null, 2));
        
        try {
          let data, error;
          
          if (tokens.format === 'new' && tokens.tokenHash) {
            // Novo formato: usar verifyOtp
            console.log('🔄 Usando verifyOtp para token_hash:', tokens.tokenHash);
            ({ data, error } = await supabase.auth.verifyOtp({
              token_hash: tokens.tokenHash,
              type: 'recovery'
            }));
            console.log('🔄 Resultado verifyOtp:', { data, error });
          } else if (tokens.format === 'old' && tokens.accessToken && tokens.refreshToken) {
            // Formato antigo: usar setSession
            console.log('🔄 Usando setSession para access_token');
            ({ data, error } = await supabase.auth.setSession({
              access_token: tokens.accessToken,
              refresh_token: tokens.refreshToken,
            }));
            console.log('🔄 Resultado setSession:', { data, error });
          }
          
          if (error) {
            console.error('❌ Erro ao definir sessão:', error);
            console.error('❌ Detalhes do erro:', JSON.stringify(error, null, 2));
            throw error;
          }
          
          if (data.session && data.user) {
            console.log('✅ Sessão configurada com sucesso para reset');
            console.log('✅ Usuário logado:', data.user.email);
            hasValidSession = true;
            setSessionReady(true);
            
            toast({
              title: "⚠️ AÇÃO OBRIGATÓRIA",
              description: "Sua senha anterior foi invalidada. Você DEVE definir uma nova senha agora para conseguir acessar sua conta.",
              variant: "default",
              duration: 10000,
            });
            
            // Limpar a URL para remover os tokens sensíveis
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          } else {
            console.log('❌ Dados de sessão/usuário ausentes:', { session: data.session, user: data.user });
          }
        } catch (error) {
          console.error('❌ Erro na configuração da sessão:', error);
          
          toast({
            title: "Link expirado ou inválido",
            description: "Este link de recuperação não é mais válido. Você será redirecionado para solicitar um novo.",
            variant: "destructive",
          });
          
          setTimeout(() => navigate('/auth?mode=reset&reason=expired_link'), 3000);
          return;
        }
      }
      
      // 4. Se chegou aqui sem tokens e sem sessão válida
      if (!hasValidSession && !tokens) {
        console.log('❌ Link sem tokens - possível problema de configuração');
        
        toast({
          title: "Link incompleto",
          description: "O link de recuperação parece estar incompleto. Solicite um novo link.",
          variant: "destructive",
        });
        
        setTimeout(() => navigate('/auth?mode=reset&reason=incomplete_link'), 3000);
      }
    };

    processPasswordReset();
  }, [navigate, toast]);

  const onSubmit = async (data: ResetPasswordForm) => {
    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password
      });

      if (error) {
        toast({
          title: "Erro ao redefinir senha",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      // Desativar o aviso antes de redirecionar
      setShowWarning(false);
      
      toast({
        title: "Senha redefinida com sucesso",
        description: "Sua senha foi alterada. Você será redirecionado para fazer login.",
      });

      // Fazer logout após redefinir a senha
      await supabase.auth.signOut();
      
      // Redirecionar para a página de login
      setTimeout(() => {
        navigate('/auth');
      }, 2000);

    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro ao redefinir a senha. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-secondary/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Redefinir Senha</CardTitle>
          <CardDescription>
            Digite sua nova senha abaixo
          </CardDescription>
        </CardHeader>
        
        {/* Aviso importante */}
        {sessionReady && showWarning && (
          <div className="mx-6 mb-4 p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg">
            <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
              ⚠️ <strong>IMPORTANTE:</strong> Sua senha anterior foi invalidada quando você clicou no link de recuperação. 
              Você <strong>DEVE</strong> definir uma nova senha agora. Se sair desta página sem definir uma nova senha, 
              você não conseguirá fazer login na sua conta.
            </p>
          </div>
        )}
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Digite sua nova senha"
                  {...register("password")}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirme sua nova senha"
                  {...register("confirmPassword")}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Redefinindo..." : "Redefinir Senha"}
            </Button>
          </form>
          
          {/* Link de emergência */}
          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-sm text-muted-foreground mb-2">
              Problemas com o link de recuperação?
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/auth?mode=reset')}
              className="w-full"
            >
              Solicitar novo link de recuperação
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}